import { useEffect, useState } from "react";
import { currencyLabel } from "../../lib/currency";
import useApi from "../../hooks/useApi";
import { Button, Input, LoadingSpinner } from "../../components/BaseComponents";
import { getCSRFToken } from "../../lib/utils";
import useCleanup from "../../hooks/useCleanup";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import ISICSelector from "../../components/BaseComponents/ISICSelector/ISICSelector";
import InputCheckbox from "../../components/BaseComponents/InputCheckbox";
import InlineAlert from "../../components/BaseComponents/Alert/InlineAlert";

type AdminCompanySummaryProps = {
    companyId: string;
}

const formatNumber = Intl.NumberFormat().format;

function AdminCompanySummary({ companyId }: AdminCompanySummaryProps) {
    const [data, setData] = useState<any>();
    const api = useApi();

    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteCofirmationOpen, setDeleteConfirmatinOpen] = useState(false);

    const [isUpdating, setIsUpdating] = useState(false);

    const [industryCodes, setIndustryCodes] = useState<string[]>([]);

    const [name, setName] = useState("");
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [tagError, setTagError] = useState("");

    const updateIndustryCodes = async () => {
        setIsUpdating(true);
        await api.put(`/admin/company/${companyId}`, {
            _csrf: await getCSRFToken(),
            company: {
                industryCodes,
            }
        });
        setIsUpdating(false);
    };

    const updateName = async () => {
        setIsUpdatingName(true);
        await api.put(`/admin/company/${companyId}`, {
            _csrf: await getCSRFToken(),
            company: {
                name,
            }
        });
        await getData();
        setIsUpdatingName(false);
    };

    const updateTag = async (tag: 'isPublicBody' | 'isRegistered' | 'isFinancialInstitution', value: boolean) => {
        setTagError("");
        try {
            await api.put(`/admin/company/${companyId}`, {
                _csrf: await getCSRFToken(),
                company: { [tag]: value },
            });
        } catch (error: any) {
            setTagError(error?.body?.error || "Could not update the tag");
            return;
        }
        await getData();
    };

    const getData = useCleanup(() => {
        let cancelled = false;
        const run = async () => {
            const result = await api.get(`admin/company/${companyId}/summary`);
            setData(result.data);
            setIndustryCodes(result.data?.isicCode ? [result.data?.isicCode] : [])
            setName(result.data?.company?.name || "")
        };
        run();
        return () => {
            cancelled = true;
        }
    })
    useEffect(() => {
        getData()
    }, [companyId])


    const onDeleteClick = () => {
        setDeleteConfirmatinOpen(true);
    }

    const onDelete = async () => {
        setIsDeleting(true);
        setDeleteConfirmatinOpen(false);
        await api.delete(`/admin/company/${companyId}/data`, {
            _csrf: await getCSRFToken()
        });
        await getData();

        setIsDeleting(false);
    }

    if(!data) {
        return <LoadingSpinner />
    }
    return (
        <div>
            <dl className='mt-3 grid grid-cols-3 text-white'>
                <dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>Organisation Name</dt>
                <dd className='col-span-2 p-4 border-y border-charcoal-200'>
                    <div className="flex space-x-2 items-end">
                        { isUpdatingName ? (
                            <LoadingSpinner inline />
                        ) : (
                            <>
                                <div className="flex-grow">
                                    <Input
                                        type="text"
                                        name="companyName"
                                        label="Organisation Name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <Button type="Primary" text="Update" size="md"
                                        accessibilityLabel="organisation name"
                                        disabled={!name.trim() || name === data.company.name}
                                        onClick={() => updateName()}/>
                                </div>
                            </>
                        )}
                    </div>
                </dd>

                <dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>iSumio Internal ID</dt>
                <dd className='col-span-2 p-4 border-y border-charcoal-200'>
                    { data.company.id }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>Organisation number</dt>
                <dd className='col-span-2 p-4 border-y border-charcoal-200'>
                    { data.company.number || '-'}
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Organisation tags</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    { [
                        { tag: 'isPublicBody', column: 'is_public_body', label: 'Public body' },
                        { tag: 'isRegistered', column: 'is_registered', label: 'Registered' },
                        { tag: 'isFinancialInstitution', column: 'is_financial_institution', label: 'Financial institution' },
                    ].map(({ tag, column, label }) => (
                        <label key={tag} className='flex items-center space-x-2'>
                            <InputCheckbox
                                checked={!!data.company[column]}
                                disabled={data.company.category === 'fictitious'}
                                onChange={(e) => updateTag(tag as 'isPublicBody' | 'isRegistered' | 'isFinancialInstitution', e.target.checked)}
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                    { tagError && <InlineAlert type='warning' className='mt-6'>{ tagError }</InlineAlert> }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Jurisdiction</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    {data.company.jurisdiction_code || '-'}
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Currency Code</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    { currencyLabel(data.company.currency_code) }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Address</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    { data.company.address || '-' }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>ISIC Code</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    <div className="flex space-x-2">
                        { isUpdating ? (
                            <LoadingSpinner inline />
                        ) : (
                            <>
                                <div className="flex-grow text-charcoal-900">
                                    <ISICSelector
                                        darkMode={true}
                                        min={1}
                                        max={1}
                                        value={industryCodes}
                                        updateHandler={setIndustryCodes}
                                    />
                                </div>
                                <div>
                                    <Button type="Primary" text="Update" size="md"
                                        onClick={() => updateIndustryCodes()}/>
                                </div>
                            </>
                        )}
                    </div>
                </dd>
            </dl>
            <div className="text-white">Organisation Data</div>
            { isDeleting 
            ? (
                <LoadingSpinner/>
            ) : (
                <dl className='mt-3 grid grid-cols-3 text-white'>
                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Accounting Periods</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        { formatNumber(data.periodCount || 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Suppliers</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        { formatNumber(data.supplierCount || 0)}
                    </dd>


                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Items</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        { formatNumber(data.itemCount|| 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Purchases</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        {formatNumber(data.purchaseCount|| 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Projects</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        { formatNumber(data.projectCount || 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Reports</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        { formatNumber(data.reportCount || 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Portfolios</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        { formatNumber(data.portfolioCount || 0)}
                    </dd>
                </dl>
            )}
            <Button
                additionalClass="mt-4" type="Primary" text="Delete Organisation Data"
                onClick={onDeleteClick}
                disabled={isDeleting}
            />
            <ConfirmationModal
                open={deleteCofirmationOpen}
                onCancel={() => setDeleteConfirmatinOpen(false)}
                onConfirm={onDelete}
                requireTypedConfirmation="delete"
            >
                <PageHeadings title="Confirm Deletion of Organisation Data" />
                <p className="text-white mt-8">
                    Confirm deleting the following data for <strong>{data.company.name}</strong>?
                </p>

                <dl className='mt-3 grid grid-cols-3 text-white'>
                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Accounting Periods</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        { formatNumber(data.periodCount || 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Suppliers</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        { formatNumber(data.supplierCount || 0)}
                    </dd>


                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Items</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        { formatNumber(data.itemCount|| 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Purchases</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        {formatNumber(data.purchaseCount|| 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Projects</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        { formatNumber(data.projectCount || 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Reports</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        { formatNumber(data.reportCount || 0)}
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Portfolios</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        { formatNumber(data.portfolioCount || 0)}
                    </dd>
                </dl>
            </ConfirmationModal>
        </div>
    )
}

export default AdminCompanySummary;
